import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipboardList, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getExecucao,
  responderPerguntas,
  finalizarExecucao,
  savePlano,
} from '../services/api';
import ConfirmDialog from '../../../common/ConfirmDialog';

// --- Ícones SVG (originais) ---
const IconRenderer = ({ children, className = "h-6 w-6" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className={className}>
    {children}
  </svg>
);
const Sitemap = ({ className }) => <IconRenderer className={className}><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M7 13h4" /><path d="M13 13h4" /><path d="M13 17h4" /><path d="M7 7h4" /><path d="M7 17h4" /></IconRenderer>;
const AlertTriangle = ({ className }) => <IconRenderer className={className}><path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><line x1="12" x2="12" y1="9" y2="13" /><line x1="12" x2="12.01" y1="17" y2="17" /></IconRenderer>;
const MessageSquare = ({ className }) => <IconRenderer className={className}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></IconRenderer>;

const buttonAnim = {
  initial: { opacity: 0, y: 20, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.3, type: 'spring', stiffness: 120 } },
  whileHover: { scale: 1.04, boxShadow: '0 4px 24px 0 rgba(39, 94, 254, 0.10)' },
  whileTap: { scale: 0.97 }
};

const ExecucaoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [execucao, setExecucao] = useState(null);
  const [respostas, setRespostas] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [commentingQuestionId, setCommentingQuestionId] = useState(null);

  // >> NOVO: mapa de planos por resposta_id
  const [planosPorRespostaId, setPlanosPorRespostaId] = useState({});
  const [planningData, setPlanningData] = useState({ perguntaId: null, respostaId: null });
  const [planText, setPlanText] = useState('');

  const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);
  const [confirmFinishOpen, setConfirmFinishOpen] = useState(false);

  const secoes = useMemo(() => execucao?.checklist_modelos?.checklist_secoes_modelo || [], [execucao]);

  const fetchExecutionData = async () => {
    try {
      const data = await getExecucao(id);
      setExecucao(data);
      const respostasIniciais = {};
      data.respostas.forEach(r => {
        respostasIniciais[r.pergunta_id] = {
          resposta_id: r.resposta_id,
          valor_resposta: r.valor_resposta,
          comentarios: r.comentarios || '',
          // se já existir plano no mapa (ex. após salvar), reflita no objeto da pergunta
          plano_acao: planosPorRespostaId[r.resposta_id] || ''
        };
      });
      setRespostas(respostasIniciais);
      return respostasIniciais;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchExecutionData().finally(() => setLoading(false));
    // Omite `fetchExecutionData`; recarrega quando o `id` da execução muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const pendingItems = useMemo(() => {
    if (!execucao) return [];
    const pendencias = [];
    secoes.forEach((secao, index) => {
      const perguntasObrigatorias = secao.checklist_perguntas_modelo.filter(p => p.obrigatoria);
      let count = 0;
      for (const pergunta of perguntasObrigatorias) {
        if (!respostas[pergunta.pergunta_id]?.valor_resposta) {
          count++;
        }
      }
      if (count > 0) {
        pendencias.push({ index, titulo: secao.titulo, count });
      }
    });
    return pendencias;
  }, [secoes, respostas, execucao]);

  const handleRespostaChange = (perguntaId, valor) => {
    setRespostas(prev => ({
      ...prev,
      [perguntaId]: { ...(prev[perguntaId] || {}), valor_resposta: valor }
    }));
  };

  const handleCommentChange = (perguntaId, comment) => {
    setRespostas(prev => ({
      ...prev,
      [perguntaId]: { ...(prev[perguntaId] || {}), comentarios: comment }
    }));
  };

  const handleToggleComment = (perguntaId) => {
    setCommentingQuestionId(prev => (prev === perguntaId ? null : perguntaId));
  };

  const saveSingleAnswer = async (perguntaId) => {
    const respostaAtual = respostas[perguntaId] || {};
    const payload = [{
      pergunta_id: parseInt(perguntaId),
      execucao_id: parseInt(id),
      valor_resposta: respostaAtual.valor_resposta ?? null,
      comentarios: respostaAtual.comentarios || '',
      ...(respostaAtual.resposta_id && { resposta_id: respostaAtual.resposta_id })
    }];
    try {
      await responderPerguntas(id, payload);
      const novasRespostas = await fetchExecutionData();
      return novasRespostas[perguntaId];
    } catch (err) {
      toast.error(`Não foi possível salvar a resposta: ${err.message}`);
      return null;
    }
  };

  // abre/fecha área de plano e pré-preenche o texto salvo pelo resposta_id
  const handleTogglePlan = async (perguntaId) => {
    if (planningData.perguntaId === perguntaId) {
      setPlanningData({ perguntaId: null, respostaId: null });
      // NÃO limpamos o texto; ele fica no estado ligado à resposta
      return;
    }
    const respostaSalva = await saveSingleAnswer(perguntaId);
    if (respostaSalva && respostaSalva.resposta_id) {
      const rid = respostaSalva.resposta_id;
      setPlanningData({ perguntaId, respostaId: rid });
      // busca texto já salvo pelo resposta_id; se não tiver, cai para o que já está guardado por pergunta
      const existente =
        planosPorRespostaId[rid] ??
        respostas[perguntaId]?.plano_acao ??
        '';
      setPlanText(existente);
    }
  };

  const handleSavePlan = async () => {
    if (!planText.trim()) {
      toast.error('Descreva o plano antes de salvar');
      return;
    }
    if (!planningData.respostaId) {
      toast.error('ID da resposta não encontrado. Tente reabrir a ação.');
      return;
    }
    try {
      await savePlano({
        descricao: planText,
        resposta_id: planningData.respostaId,
        status: 'PENDENTE'
      });

      // >>> GUARDA o texto pelo resposta_id e também dentro do objeto da pergunta
      setPlanosPorRespostaId(prev => ({
        ...prev,
        [planningData.respostaId]: planText
      }));
      setRespostas(prev => ({
        ...prev,
        [planningData.perguntaId]: {
          ...(prev[planningData.perguntaId] || {}),
          plano_acao: planText,
          resposta_id: planningData.respostaId
        }
      }));

      toast.success('Plano de ação salvo!');
      // Mantemos a área aberta e o texto preenchido (sem limpar)
    } catch (err) {
      toast.error(`Erro ao salvar plano: ${err.message}`);
    }
  };

  const saveProgress = async () => {
    const respostasParaSalvar = Object.entries(respostas)
      .filter(([, dados]) => dados.valor_resposta !== undefined)
      .map(([pergunta_id, dados]) => {
        const payload = {
          pergunta_id: parseInt(pergunta_id),
          execucao_id: parseInt(id),
          valor_resposta: dados.valor_resposta,
          comentarios: dados.comentarios || ''
        };
        if (dados.resposta_id) payload.resposta_id = dados.resposta_id;
        return payload;
      });
    if (respostasParaSalvar.length > 0) {
      await responderPerguntas(id, respostasParaSalvar);
    }
  };

  const handleContinuarDepois = async () => {
    try {
      await saveProgress();
      toast.success('Progresso salvo com sucesso!');
      navigate('/checklist');
    } catch (err) {
      toast.error(`Erro ao salvar progresso: ${err.message}`);
    }
  };

  const confirmConcluir = async () => {
    try {
      await saveProgress();
      await finalizarExecucao(id);
      toast.success('Checklist concluído com sucesso!');
      navigate('/checklist/aplicar');
    } catch (err) {
      toast.error(`Erro ao concluir: ${err.message}`);
    } finally {
      setConfirmFinishOpen(false);
    }
  };

  const handleConcluir = () => {
    if (pendingItems.length > 0) {
      setIsPendingModalOpen(true);
      return;
    }
    setConfirmFinishOpen(true);
  };

  const handleNavigateSection = (direction) => {
    const newIndex = activeSection + direction;
    if (newIndex >= 0 && newIndex < secoes.length) setActiveSection(newIndex);
  };

  if (loading) return <div className="p-6 text-center">A carregar checklist...</div>;
  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-800">Erro ao Carregar</h2>
        <p className="text-gray-600 mt-2">{error}</p>
      </div>
    );
  }
  if (!execucao) return <div className="p-6 text-center">Execução não encontrada.</div>;

  const perguntasDaSecaoAtual = secoes[activeSection]?.checklist_perguntas_modelo || [];

  return (
    <>
      <div className="bg-white flex flex-col md:flex-row">
        {/* Sidebar (desktop) */}
        <aside className="hidden md:flex w-72 flex-col border-r bg-white">
          <div className="p-5 border-b">
            <h3 className="font-bold text-lg text-gray-900">{execucao.checklist_modelos?.titulo}</h3>
            <p className="text-xs text-gray-500">{execucao.unidades?.nome}</p>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1">
            {secoes.map((secao, index) => (
              <motion.button
                key={secao.secao_id}
                onClick={() => setActiveSection(index)}
                className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium transition ${
                  activeSection === index
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'bg-transparent text-gray-800 hover:bg-purple-50 hover:text-purple-700'
                } focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500`}
                initial={false}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Sitemap className={`w-5 h-5 mr-2 ${activeSection === index ? 'text-white' : 'text-purple-400'}`} />
                <span className="truncate">{secao.titulo}</span>
              </motion.button>
            ))}
          </nav>
          <div className="p-4 border-t space-y-2 bg-white">
            <motion.button
              onClick={() => setIsPendingModalOpen(true)}
              className="w-full py-2 rounded-md font-semibold text-yellow-900 bg-yellow-50 border border-yellow-100 hover:bg-yellow-100 focus-visible:ring-2 focus-visible:ring-yellow-300 transition"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              VER PENDÊNCIAS
            </motion.button>
            <motion.button
              onClick={handleContinuarDepois}
              className="w-full py-2 rounded-md font-semibold text-gray-800 bg-gray-50 border border-gray-200 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-purple-200 transition"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              CONTINUAR DEPOIS
            </motion.button>
            <motion.button
              onClick={handleConcluir}
              className="w-full py-2 rounded-md font-bold text-white bg-purple-600 hover:bg-purple-700 focus-visible:ring-2 focus-visible:ring-purple-400 transition"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              CONCLUIR CHECKLIST
            </motion.button>
          </div>
        </aside>

        {/* Top bar (mobile) */}
        <div className="md:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b">
          <div className="px-4 py-3 space-y-2">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {execucao.checklist_modelos?.titulo}
              </h3>
              <p className="text-xs text-gray-500 truncate">{execucao.unidades?.nome}</p>
            </div>

            <div className="flex flex-col gap-2">
              <select
                value={activeSection}
                onChange={(e) => setActiveSection(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {secoes.map((s, i) => (
                  <option key={s.secao_id} value={i}>
                    {i + 1}. {s.titulo}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsPendingModalOpen(true)}
                  className="px-3 py-2 text-sm rounded-md border border-yellow-200 bg-yellow-50 text-yellow-900 font-semibold"
                >
                  Pendências {pendingItems.length > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-500 text-white text-xs">
                      {pendingItems.reduce((acc, p) => acc + p.count, 0)}
                    </span>
                  )}
                </button>
                <button
                  onClick={handleConcluir}
                  className="ml-auto px-3 py-2 text-sm rounded-md bg-purple-600 text-white font-semibold"
                >
                  Concluir
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 flex flex-col bg-gradient-to-br from-white via-purple-50 to-white">
          <main className="flex-1 px-4 md:p-6 pt-4 md:pt-6 pb-28 md:pb-6 overflow-y-auto">
            {secoes.length > 0 && (
              <div>
                <h2 className="hidden md:block text-2xl font-bold mb-6 text-purple-900">
                  {secoes[activeSection].titulo}
                </h2>

                <div className="space-y-6 md:space-y-10">
                  {perguntasDaSecaoAtual.map(pergunta => {
                    const resposta = respostas[pergunta.pergunta_id] || {};
                    return (
                      <motion.div
                        key={pergunta.pergunta_id}
                        className="p-4 md:p-5 rounded-xl shadow-md bg-white/90 border border-purple-100"
                        {...buttonAnim}
                        initial="initial"
                        animate="animate"
                      >
                        <p className="font-semibold text-purple-900 mb-3 text-sm md:text-base">
                          {pergunta.texto_pergunta}
                          {pergunta.obrigatoria && <span className="text-red-500 ml-1">*</span>}
                        </p>

                        {/* Respostas */}
                        <div className="mb-3">
                          {pergunta.tipo_pergunta === 'MULTIPLA_ESCOLHA' && (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-3">
                              <motion.button
                                onClick={() => handleRespostaChange(pergunta.pergunta_id, 'N/A')}
                                className={`border rounded-lg py-2 md:py-3 text-sm md:text-base font-semibold text-gray-600 bg-white hover:bg-gray-100 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all ${
                                  resposta.valor_resposta === 'N/A' ? 'ring-2 ring-purple-400 bg-purple-50 border-purple-500 scale-105' : 'border-gray-200'
                                }`}
                                whileTap="whileTap"
                              >
                                N/A
                              </motion.button>
                              <motion.button
                                onClick={() => handleRespostaChange(pergunta.pergunta_id, 'NAO_CONFORME')}
                                className={`border rounded-lg py-2 md:py-3 text-2xl md:text-3xl font-bold bg-white hover:bg-red-50 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-300 transition-all ${
                                  resposta.valor_resposta === 'NAO_CONFORME' ? 'ring-2 ring-red-400 bg-red-100 border-red-500 scale-105' : 'border-gray-200'
                                }`}
                                whileTap="whileTap"
                              >
                                😟
                              </motion.button>
                              <motion.button
                                onClick={() => handleRespostaChange(pergunta.pergunta_id, 'PARCIALMENTE_CONFORME')}
                                className={`border rounded-lg py-2 md:py-3 text-2xl md:text-3xl font-bold bg-white hover:bg-yellow-50 hover:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-200 transition-all ${
                                  resposta.valor_resposta === 'PARCIALMENTE_CONFORME' ? 'ring-2 ring-yellow-400 bg-yellow-100 border-yellow-400 scale-105' : 'border-gray-200'
                                }`}
                                whileTap="whileTap"
                              >
                                😐
                              </motion.button>
                              <motion.button
                                onClick={() => handleRespostaChange(pergunta.pergunta_id, 'CONFORME')}
                                className={`border rounded-lg py-2 md:py-3 text-2xl md:text-3xl font-bold bg-white hover:bg-green-50 hover:border-green-400 focus:outline-none focus:ring-2 focus:ring-green-200 transition-all ${
                                  resposta.valor_resposta === 'CONFORME' ? 'ring-2 ring-green-400 bg-green-100 border-green-400 scale-105' : 'border-gray-200'
                                }`}
                                whileTap="whileTap"
                              >
                                🙂
                              </motion.button>
                            </div>
                          )}

                          {pergunta.tipo_pergunta === 'SIM_NAO' && (
                            <div className="grid grid-cols-2 gap-2 md:gap-3">
                              <motion.button
                                onClick={() => handleRespostaChange(pergunta.pergunta_id, 'NAO')}
                                className={`border rounded-lg py-2 md:py-3 text-sm md:text-base font-semibold text-black bg-white hover:bg-red-50 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-300 transition-all ${
                                  resposta.valor_resposta === 'NAO' ? 'ring-2 ring-red-400 bg-red-100 border-red-500 scale-105' : 'border-gray-200'
                                }`}
                                whileTap="whileTap"
                              >
                                Não
                              </motion.button>
                              <motion.button
                                onClick={() => handleRespostaChange(pergunta.pergunta_id, 'SIM')}
                                className={`border rounded-lg py-2 md:py-3 text-sm text-black md:text-base font-semibold bg-white hover:bg-green-50 hover:border-green-400 focus:outline-none focus:ring-2 focus:ring-green-200 transition-all ${
                                  resposta.valor_resposta === 'SIM' ? 'ring-2 ring-green-400 bg-green-100 border-green-400 scale-105' : 'border-gray-200'
                                }`}
                                whileTap="whileTap"
                              >
                                Sim
                              </motion.button>
                            </div>
                          )}

                          {pergunta.tipo_pergunta === 'TEXTO' && (
                            <input
                              type="text"
                              value={resposta.valor_resposta || ''}
                              onChange={(e) => handleRespostaChange(pergunta.pergunta_id, e.target.value)}
                              className="w-full border-gray-300 rounded-md px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-200"
                              placeholder="Digite sua resposta"
                            />
                          )}
                        </div>

                        {/* Ações da pergunta */}
                        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-3">
                          <motion.button
                            onClick={() => handleToggleComment(pergunta.pergunta_id)}
                            className={`border rounded-lg py-2 text-sm md:text-base flex items-center justify-center font-medium transition-all shadow-sm focus:ring-2 focus:ring-purple-200 ${
                              resposta.comentarios ? 'bg-purple-100 text-purple-700 border-purple-300' : 'text-purple-900 bg-white hover:bg-purple-50 hover:border-purple-400'
                            }`}
                            whileHover="whileHover"
                            whileTap="whileTap"
                          >
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Comentário
                          </motion.button>

                          <motion.button
                            onClick={() => handleTogglePlan(pergunta.pergunta_id)}
                            className={`border rounded-lg py-2 text-sm md:text-base flex items-center justify-center font-medium transition-all shadow-sm focus:ring-2 focus:ring-green-200 ${
                              planningData.perguntaId === pergunta.pergunta_id ? 'bg-green-100 text-green-700 border-green-400' : 'text-green-900 bg-white hover:bg-green-50 hover:border-green-400'
                            }`}
                            whileHover="whileHover"
                            whileTap="whileTap"
                          >
                            <ClipboardList className="h-4 w-4 mr-2" />
                            Ação
                          </motion.button>
                        </div>

                        {/* Campos extras */}
                        <AnimatePresence>
                          {commentingQuestionId === pergunta.pergunta_id && (
                            <motion.div
                              className="mt-3"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 10 }}
                              transition={{ duration: 0.25 }}
                            >
                              <textarea
                                value={resposta.comentarios || ''}
                                onChange={(e) => handleCommentChange(pergunta.pergunta_id, e.target.value)}
                                className="w-full border-gray-300 rounded-md focus:ring-2 focus:ring-purple-200 text-sm md:text-base"
                                rows={3}
                                placeholder="Adicione as suas observações..."
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <AnimatePresence>
                          {planningData.perguntaId === pergunta.pergunta_id && (
                            <motion.div
                              className="mt-3"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 10 }}
                              transition={{ duration: 0.25 }}
                            >
                              <textarea
                                value={planText}
                                onChange={(e) => setPlanText(e.target.value)}
                                className="w-full border-gray-300 rounded-md focus:ring-2 focus:ring-green-200 text-sm md:text-base"
                                rows={3}
                                placeholder="Descreva o plano de ação..."
                              />
                              <div className="mt-2 text-right">
                                <motion.button
                                  onClick={handleSavePlan}
                                  className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-700 text-white rounded-md font-semibold shadow-lg hover:from-green-600 hover:to-green-800 focus:ring-2 focus:ring-green-200 transition-all"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  Salvar Plano
                                </motion.button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </main>

          {/* Footer navegação (desktop) */}
          <footer className="hidden md:flex p-4 border-t bg-white justify-between items-center">
            <motion.button
              onClick={() => handleNavigateSection(-1)}
              disabled={activeSection === 0}
              className="px-7 py-2 rounded-lg font-bold border transition-all text-purple-900 bg-white hover:bg-purple-50 border-purple-100 disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover="whileHover"
              whileTap="whileTap"
            >
              ANTERIOR
            </motion.button>
            <span className="text-base text-gray-500">
              {activeSection + 1} de {secoes.length}
            </span>
            <motion.button
              onClick={() => handleNavigateSection(1)}
              disabled={activeSection === secoes.length - 1}
              className="px-7 py-2 rounded-lg font-bold border transition-all text-purple-900 bg-white hover:bg-purple-50 border-purple-100 disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover="whileHover"
              whileTap="whileTap"
            >
              PRÓXIMO
            </motion.button>
          </footer>
        </div>

        {/* Bottom bar (mobile) */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t bg-white">
          <div className="px-4 py-2 flex items-center justify-between">
            <button
              onClick={() => handleNavigateSection(-1)}
              disabled={activeSection === 0}
              className="px-4 py-2 rounded-md text-sm font-semibold border border-purple-100 bg-white text-purple-900 disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="text-xs text-gray-500">{activeSection + 1} / {secoes.length}</span>
            <button
              onClick={() => handleNavigateSection(1)}
              disabled={activeSection === secoes.length - 1}
              className="px-4 py-2 rounded-md text-sm font-semibold border border-purple-100 bg-white text-purple-900 disabled:opacity-50"
            >
              Próximo
            </button>
          </div>
          <div className="px-4 pb-4 grid grid-cols-3 gap-2">
            <button
              onClick={() => setIsPendingModalOpen(true)}
              className="col-span-1 px-3 py-2 rounded-md text-xs font-semibold border border-yellow-200 bg-yellow-50 text-yellow-900"
            >
              Pendências
            </button>
            <button
              onClick={handleContinuarDepois}
              className="col-span-1 px-3 py-2 rounded-md text-xs font-semibold border bg-gray-50 border-gray-200 text-gray-800"
            >
              Salvar
            </button>
            <button
              onClick={handleConcluir}
              className="col-span-1 px-3 py-2 rounded-md text-xs font-bold bg-purple-600 text-white"
            >
              Concluir
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Pendências */}
      <AnimatePresence>
        {isPendingModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full max-w-md border-2 border-purple-200"
              initial={{ y: 70, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 70, opacity: 0 }}
              transition={{ duration: 0.28, type: "spring" }}
            >
              {pendingItems.length > 0 ? (
                <>
                  <h3 className="text-xl md:text-2xl font-bold text-center mb-2 text-purple-900">Itens Pendentes</h3>
                  <p className="text-center text-gray-700 mb-6 text-sm md:text-base">
                    Existem itens obrigatórios pendentes. Selecione uma seção para revisar.
                  </p>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {pendingItems.map(item => (
                      <motion.button
                        key={item.index}
                        onClick={() => { setActiveSection(item.index); setIsPendingModalOpen(false); }}
                        className="w-full flex justify-between items-center p-3 border border-purple-300 rounded-lg bg-purple-50 font-semibold text-purple-900 hover:bg-purple-100 transition-all"
                        whileHover={{ scale: 1.02 }}
                      >
                        <span className="hover:underline text-left">{item.titulo}</span>
                        <span className="bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">{item.count}</span>
                      </motion.button>
                    ))}
                  </div>
                  <div className="mt-6 flex justify-end">
                    <motion.button
                      onClick={() => setIsPendingModalOpen(false)}
                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg font-bold hover:from-purple-600 hover:to-purple-700 focus:ring-2 focus:ring-purple-200 transition-all"
                      whileHover="whileHover"
                    >
                      Voltar
                    </motion.button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center">
                    <CheckCircle className="h-14 w-14 md:h-16 md:w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl md:text-2xl font-bold mb-2 text-gray-800">Tudo Certo!</h3>
                    <p className="text-gray-600 mb-6 text-sm md:text-base">Nenhuma pendência obrigatória foi encontrada.</p>
                  </div>
                  <div className="mt-6 flex justify-center">
                    <motion.button
                      onClick={() => setIsPendingModalOpen(false)}
                      className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-bold hover:from-green-600 hover:to-green-700 focus:ring-2 focus:ring-green-200 transition-all"
                      whileHover="whileHover"
                    >
                      OK
                    </motion.button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={confirmFinishOpen}
        title="Concluir Checklist"
        message="Deseja realmente concluir este checklist?"
        onConfirm={confirmConcluir}
        onClose={() => setConfirmFinishOpen(false)}
      />
    </>
  );
};

export default ExecucaoForm;